/**
 * The Poroboard look (runebound-app `apps/web/src/features/poroboard/builder/look.ts` and its table), shared by every
 * screen of Runebound Play: matte blue-black surfaces, thin neutral edges, a flat violet primary, and each side in its
 * colour (you cyan-blue, the opponent red) with the shared zones (battlefields, chain) in muted gold.
 *
 * Written out as values (not CSS variables) because inline styles and SVG need them.
 */

/** Seat colours: `you` is the viewer's side, `opponent` the other. */
export type Side = 'you' | 'opponent';

export const LOOK = {
  page: '#0C1015',
  panel: '#12171D',
  raised: '#161f2a',
  raisedHi: '#1c2633',
  border: '#293442',
  divider: 'rgba(255,255,255,0.06)',
  text: '#F1F4F8',
  sub: '#AAB6C8',
  muted: '#7D889A',
  faint: '#5f6a7a',
  gold: '#d9a441',
  violet: '#8b5cf6',
  violetSoft: 'rgba(139,92,246,0.14)',
  ok: '#2fd38b',
  warn: '#ffc400',
  bad: '#ff5c7a',
} as const;

/** The side panels' fill (the editor's sidebar, menus). */
export const SURFACE = '#111923';
/** The sunken fill for tracks, chips, tiles and the tab band. */
export const WELL = '#0C121A';
/** The table's colour: the whole play area is this, edge to edge. */
export const TABLE_BG = '#141b26';
/** Hairline between bars and panels. */
export const BAR_BORDER = 'rgba(255,255,255,0.06)';
/** The flat violet primary (Play, Start, Sign in). */
export const PRIMARY: React.CSSProperties = {
  background: '#5a3ff0',
  boxShadow: 'inset 0 0 0 1px #7558ff',
  color: '#fff',
};
/** A dark control on a bar (pills, menus). */
export const PILL: React.CSSProperties = {
  background: 'rgba(12,16,22,0.72)',
  boxShadow: `inset 0 0 0 1px ${LOOK.border}`,
};

/** Each side's name colour, dot and soft fill. */
export const SIDE_LOOK: Record<Side, { hue: string; soft: string; text: string }> = {
  you: { hue: '#00d8df', soft: 'rgba(0,216,223,0.10)', text: '#3fe6ec' },
  opponent: { hue: '#ff405c', soft: 'rgba(255,64,92,0.10)', text: '#ff5c74' },
};

const SIDE_RGB: Record<Side, string> = { you: '0,216,223', opponent: '255,64,92' };
/** A side's colour at alpha `a` (zone borders 0.75, quiet 0.55, inert 0.34; labels 0.95). */
export const sideLine = (side: Side, a: number) => `rgba(${SIDE_RGB[side]},${a})`;

/** The shared zones' muted gold (battlefields, chain). */
export const GOLD_LINE = {
  strong: 'rgba(201,170,110,0.3)',
  soft: 'rgba(201,170,110,0.21)',
  dim: 'rgba(201,170,110,0.13)',
  label: 'rgba(214,196,160,0.55)',
} as const;
